/**
 * Utilidades para FCM Web: Chrome exige userVisibleOnly en suscripciones push.
 * Suscripciones antiguas o comprobaciones incorrectas (p.ej. PushManager.permissionState)
 * pueden provocar AbortError código 20 / "push service error".
 */

function shouldRetryFcmTokenError(error) {
  const msg = String(error?.message || '').toLowerCase()
  const name = String(error?.name || '').toLowerCase()
  const code = error?.code
  return (
    code === 20 ||
    code === '20' ||
    name === 'aborterror' ||
    msg.includes('registration failed') ||
    msg.includes('push service') ||
    msg.includes('uservisibleonly') ||
    msg.includes('token-subscribe') ||
    msg.includes('abort')
  )
}

/** Solo las opciones que acepta Firebase getToken */
function toGetTokenOptions(options) {
  return {
    vapidKey: options.vapidKey,
    serviceWorkerRegistration: options.serviceWorkerRegistration
  }
}

/**
 * Elimina la suscripción push actual del SW (si existe).
 * Útil antes de un nuevo getToken cuando Chrome devuelve código 20.
 */
export async function unsubscribeExistingPushSubscription(registration) {
  if (!registration?.pushManager) return false
  try {
    const sub = await registration.pushManager.getSubscription()
    if (sub) {
      await sub.unsubscribe()
      console.info('[FCM] Suscripción push anterior eliminada')
      return true
    }
  } catch (e) {
    console.warn('[FCM] No se pudo eliminar suscripción push anterior:', e)
  }
  return false
}

/**
 * Llama a Firebase getToken; opcionalmente limpia la suscripción ANTES del primer intento
 * (recomendado al pulsar "Activar notificaciones"). Si falla, desuscribe y reintenta una vez.
 *
 * @param {function} getToken - firebase/messaging getToken
 * @param {object} messaging - instancia Messaging
 * @param {{
 *   vapidKey: string,
 *   serviceWorkerRegistration: ServiceWorkerRegistration,
 *   clearSubscriptionFirst?: boolean
 * }} options
 */
export async function getFcmTokenWithSubscriptionRetry(getToken, messaging, options) {
  const tokenOpts = toGetTokenOptions(options)
  const { clearSubscriptionFirst = false } = options

  if (clearSubscriptionFirst && tokenOpts.serviceWorkerRegistration?.pushManager) {
    await unsubscribeExistingPushSubscription(tokenOpts.serviceWorkerRegistration)
    await new Promise((r) => setTimeout(r, 450))
  }

  try {
    return await getToken(messaging, tokenOpts)
  } catch (error) {
    if (!shouldRetryFcmTokenError(error) || !tokenOpts.serviceWorkerRegistration?.pushManager) {
      throw error
    }

    await unsubscribeExistingPushSubscription(tokenOpts.serviceWorkerRegistration)
    await new Promise((r) => setTimeout(r, 450))

    try {
      return await getToken(messaging, tokenOpts)
    } catch (secondError) {
      if (!shouldRetryFcmTokenError(secondError)) {
        throw secondError
      }
      // Último intento tras pausa (a veces el servicio push de Chrome va lento)
      await new Promise((r) => setTimeout(r, 900))
      return await getToken(messaging, tokenOpts)
    }
  }
}
